<script setup lang="ts">
import { computed, ref } from 'vue';
import type { ServerInterface } from '@atum-remote/protocol/server-inspection';
import { bytes } from '../lib/server-inspection.js';
import { isIncidental, linkSpeed, rate } from '../lib/server-resources.js';

const props = defineProps<{
  network: readonly ServerInterface[];
  rates: ReadonlyMap<string, { received: number; sent: number }>;
}>();
const showAll = ref(false);
const main = computed(() => props.network.filter((n) => !isIncidental(n)));
const incidental = computed(() => props.network.filter((n) => isIncidental(n)));
const rows = computed(() => (showAll.value ? [...main.value, ...incidental.value] : main.value));
const kindLabel: Record<string, string> = {
  loopback: 'Loopback',
  physical: 'Ethernet',
  wireless: 'Wireless',
  bridge: 'Bridge',
  bond: 'Bond',
  vlan: 'VLAN',
  virtual: 'Virtual',
  tunnel: 'Tunnel',
};
function addresses(item: ServerInterface) {
  const list = (item.addresses ?? []).filter((a) => a.scope !== 'link');
  return { shown: list.slice(0, 2), rest: list.slice(2) };
}
function state(item: ServerInterface): { text: string; tone: 'ok' | 'warn' | 'muted' } {
  if (!item.state) return { text: '—', tone: 'muted' };
  if (item.state === 'up') return { text: 'Up', tone: 'ok' };
  if (item.state === 'down')
    return {
      text: 'Down',
      tone:
        ['physical', 'bond'].includes(item.kind ?? '') &&
        (item.defaultRoute?.length || item.addresses?.length)
          ? 'warn'
          : 'muted',
    };
  return { text: item.state === 'unknown' ? 'Unknown' : item.state, tone: 'muted' };
}
</script>

<template>
  <section class="panel res-section" aria-labelledby="res-network-title">
    <div class="res-section-head">
      <div>
        <h3 id="res-network-title">Network</h3>
        <p>Live rates between readings. Totals reset when an interface restarts.</p>
      </div>
    </div>
    <div class="server-table res-table">
      <table>
        <thead>
          <tr>
            <th>Interface</th>
            <th>Link</th>
            <th>Addresses</th>
            <th class="num">Receive</th>
            <th class="num">Send</th>
            <th class="num">Since reset</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in rows" :key="item.name" :data-muted="isIncidental(item) || undefined">
            <td>
              <span class="res-name"
                ><span class="mono">{{ item.name }}</span
                ><span v-if="item.defaultRoute?.length" class="tag res-tone" data-tone="accent"
                  >Default route</span
                ></span
              >
              <span v-if="item.kind" class="res-sub">{{ kindLabel[item.kind] }}</span>
            </td>
            <td>
              <span class="res-state" :data-tone="state(item).tone">{{ state(item).text }}</span>
              <span v-if="linkSpeed(item.speedMbps)" class="res-sub">{{
                linkSpeed(item.speedMbps)
              }}</span>
            </td>
            <td>
              <span v-for="a in addresses(item).shown" :key="a.address" class="mono res-address"
                >{{ a.address }}<span class="res-faint">/{{ a.prefix }}</span></span
              >
              <span
                v-if="addresses(item).rest.length"
                class="tag"
                :title="
                  addresses(item)
                    .rest.map((a) => `${a.address}/${a.prefix}`)
                    .join('\n')
                "
                >+{{ addresses(item).rest.length }}</span
              >
              <span v-if="!item.addresses" class="res-faint">—</span>
            </td>
            <td class="num">{{ rate(rates.get(item.name)?.received) }}</td>
            <td class="num">{{ rate(rates.get(item.name)?.sent) }}</td>
            <td class="num res-faint">{{ bytes(item.received) }} / {{ bytes(item.sent) }}</td>
          </tr>
        </tbody>
      </table>
    </div>
    <button
      v-if="incidental.length"
      type="button"
      class="quiet small res-more"
      :aria-expanded="showAll ? 'true' : 'false'"
      @click="showAll = !showAll"
    >
      {{
        showAll
          ? 'Hide loopback and virtual interfaces'
          : `Show ${incidental.length} loopback and virtual ${incidental.length === 1 ? 'interface' : 'interfaces'}`
      }}
    </button>
  </section>
</template>
